/**
 * Color utility functions for diagram visualization
 * Handles dynamic color assignment and gradient generation for area nodes
 */

// TypeScript interfaces
interface ApiData {
  name: string;
  type?: string;
  developerProgram?: boolean;
  children?: ApiData[];
  [key: string]: any;
}

interface LegendItem {
  name: string;
  color: string;
}

interface AreaColorMap {
  [areaName: string]: string;
}

// Named color constants for better maintainability
const COLORS = {
  RED: '#FF6B6B',
  TEAL: '#98D8C8',
  BLUE: '#45B7D1',
  GREEN: '#96CEB4',
  YELLOW: '#FFEAA7',
  PURPLE: '#DDA0DD',
  ORANGE: '#FF9F43',
  VIOLET: '#A55EEA',
  CYAN: '#26D0CE',
  PINK: '#FC427B',
  LIGHT_BLUE: '#87CEEB',
  LIGHT_SALMON: '#FFA07A',
  MINT: '#4ECDC4',
  LIGHT_YELLOW: '#F7DC6F',
  LIGHT_PURPLE: '#BB8FCE',
  LIGHT_BLUE_2: '#85C1E9',
  LIGHT_GREEN: '#82E0AA',
  LIGHT_ORANGE: '#F8C471',
  LIGHT_RED: '#EC7063',
  PALE_BLUE: '#AED6F1',
  DEFAULT_GRAY: '#95A5A6',
  MOTOROLA_GRAY: '#b4b4b4ff'
};

// Dynamic color palette for areas - will auto-assign colors to any area names
const COLOR_PALETTE = [
  COLORS.RED,
  COLORS.TEAL,
  COLORS.BLUE,
  COLORS.GREEN,
  COLORS.YELLOW,
  COLORS.PURPLE,
  COLORS.ORANGE,
  COLORS.VIOLET,
  COLORS.CYAN,
  COLORS.PINK,
  COLORS.LIGHT_BLUE,
  COLORS.LIGHT_SALMON,
  COLORS.MINT,
  COLORS.LIGHT_YELLOW,
  COLORS.LIGHT_PURPLE,
  COLORS.LIGHT_BLUE_2,
  COLORS.LIGHT_GREEN,
  COLORS.LIGHT_ORANGE,
  COLORS.LIGHT_RED,
  COLORS.PALE_BLUE
];

/**
 * Creates and manages color assignments for areas
 */
export class ColorManager {
  private areaBaseColors: AreaColorMap;

  constructor() {
    this.areaBaseColors = {};
  }

  /**
   * Get or assign color for an area
   * @param {string} areaName - The name of the area
   * @returns {string} - Hex color code
   */
  getAreaColor(areaName: string): string {
    if (!areaName) return COLORS.DEFAULT_GRAY; // Default gray if no area
    
    // If color already assigned, return it
    if (this.areaBaseColors[areaName]) {
      return this.areaBaseColors[areaName];
    }
    
    // Assign new color from palette
    const currentAreaCount = Object.keys(this.areaBaseColors).length;
    const colorIndex = currentAreaCount % COLOR_PALETTE.length;
    this.areaBaseColors[areaName] = COLOR_PALETTE[colorIndex];
    
    return this.areaBaseColors[areaName];
  }

  /**
   * Get all assigned area colors
   * @returns {Object} - Object mapping area names to colors
   */
  getAllAreaColors(): AreaColorMap {
    return { ...this.areaBaseColors };
  }

  /**
   * Reset all color assignments
   */
  reset(): void {
    this.areaBaseColors = {};
  }
}

/**
 * Get area name from a node by traversing up to level 1
 * @param {any} d - D3 hierarchy node
 * @returns {string|null} - Area name or null
 */
export const getAreaName = (d: any): string | null => {
  let current = d;
  while (current.depth > 1 && current.parent) {
    current = current.parent;
  }
  return current.depth === 1 ? current.data.name : null;
};

/**
 * Create gradient colors based on depth
 * @param {string} baseColor - Base hex color
 * @param {number} depth - Node depth
 * @returns {string} - Gradient hex color
 */
export const getGradientColor = (baseColor: string, depth: number): string => {
  if (depth <= 1) return baseColor;
  
  // Convert hex to RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate darkness factor (gets darker with each level)
  const darknessFactor = Math.pow(0.8, depth - 1);
  
  // Apply darkness
  const newR = Math.round(r * darknessFactor);
  const newG = Math.round(g * darknessFactor);
  const newB = Math.round(b * darknessFactor);
  
  // Convert back to hex
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

/**
 * Get node color based on type and properties
 * @param {any} d - D3 hierarchy node
 * @param {ColorManager} colorManager - Color manager instance
 * @returns {string} - Hex color code
 */
export const getNodeColor = (d: any, colorManager: ColorManager): string => {
  if (d.depth === 0) return COLORS.MOTOROLA_GRAY; // Root node - Motorola gray
  
  // Check if this is an API node or area node
  if (d.data.type === 'api') {
    // Color based on developer program status (optional differentiation)
    return d.data.developerProgram ? COLORS.MOTOROLA_GRAY : COLORS.MOTOROLA_GRAY;
  } else {
    // Area nodes - get base color from area and apply gradient based on depth
    const areaName = getAreaName(d);
    const baseColor = colorManager.getAreaColor(areaName || '');
    
    return getGradientColor(baseColor, d.depth);
  }
};

/**
 * Generate legend items from tree data
 * @param {any} rootNode - D3 hierarchy root node
 * @param {ColorManager} colorManager - Color manager instance
 * @returns {LegendItem[]} - Array of legend items with name and color
 */
export const generateLegendItems = (rootNode: any, colorManager: ColorManager): LegendItem[] => {
  const areas = new Set<string>();
  const dynamicLegendItems: LegendItem[] = [];
  
  // Collect all level 1 areas and their colors
  rootNode.children?.forEach((child: any) => {
    if (child.depth === 1) {
      const areaName = child.data.name;
      if (!areas.has(areaName)) {
        areas.add(areaName);
        const color = colorManager.getAreaColor(areaName);
        dynamicLegendItems.push({
          name: areaName,
          color: color
        });
      }
    }
  });
  
  // Sort legend items for consistent display
  dynamicLegendItems.sort((a, b) => a.name.localeCompare(b.name));
  return dynamicLegendItems;
};

export default ColorManager;

// Export color constants for use in other components if needed
export { COLORS };
